import { baseApi } from "@/services/api";

import { Device } from "@/features/devices/types";
import {
  DEVICE_DETAIL_KEEP_UNUSED_DATA_SECONDS,
  DeviceDetailResponse,
} from "@/features/devices/deviceDetailDisplay";
import {
  buildAssignZoneChannelRequest,
  buildZoneChannelPath,
  type ZoneChannelAssignmentSnapshot,
} from "./zoneChannelAssignment";

const apiWithTags = baseApi.enhanceEndpoints({});

interface GetFiltersQueryArgs {
  page?: number;
  pageSize?: number;
  params?: Record<string, any>;
}

/** V2 SA Device Fleet allowlisted remote commands. */
export const V2_DEVICE_COMMANDS = [
  "update_kk_app",
  "update_assistant_app",
  "restart_kk_app",
  "restart_assistant_app",
  "reboot_device",
  "update_kk_screensaver",
  "update_kk_package",
  "refresh_software_inventory",
  "update_kk_app_meta",
  "report_kk_app_meta",
  "update_kk_music_schedule",
  "report_kk_music_schedule",
  "report_device_info",
  "test_download_speed",
  "set_timezone",
] as const;

export type V2DeviceCommand = (typeof V2_DEVICE_COMMANDS)[number];

export const isV2DeviceCommand = (command: string): command is V2DeviceCommand =>
  (V2_DEVICE_COMMANDS as readonly string[]).includes(command);

/** Pure path builder for GET /v1/sa/device-fleet/devices (never legacy /devices). */
export function buildDevicesListPath({
  params,
  page,
  pageSize,
}: GetFiltersQueryArgs): string {
  const queryParams: string[] = [];

  if (params && typeof params === "object") {
    Object.entries(params).forEach(([key, value]) => {
      if (value !== null && value !== undefined && value !== "") {
        queryParams.push(`${key}=${encodeURIComponent(String(value))}`);
      }
    });
  }

  if (page !== undefined) queryParams.push(`page=${page}`);
  if (pageSize !== undefined) queryParams.push(`pageSize=${pageSize}`);

  const qs = queryParams.length ? `?${queryParams.join("&")}` : "";
  return `v1/sa/device-fleet/devices${qs}`;
}

/**
 * Pure request builder for POST /v1/sa/device-fleet/{deviceId}/command.
 * deviceId must be the canonical V2 string id (never a CheckedDevice object).
 * Never include androidId or client-supplied media URLs.
 */
export type DeviceCommandSettingsBody = {
  volume?: number;
  master?: number;
  appLock?: boolean;
  appLockPin?: string;
  wakeSleep?: boolean;
  startTime?: string;
  endTime?: string;
  /** T24 — explicit true/false whenever wake/sleep settings are sent. */
  sleepSaturday?: boolean;
  sleepSunday?: boolean;
};

/** T24 — one Music Scheduler slot (HH:mm local time). */
export type MusicScheduleSlotBody = {
  channelId: string;
  channelName: string;
  time: string;
};

/**
 * T24 — update_kk_music_schedule payload (Fleet contract):
 * { enabled, scheduleRevision, slot1, slot2 }. A slot is null when unset.
 */
export type MusicScheduleCommandBody = {
  enabled: boolean;
  scheduleRevision: number;
  slot1: MusicScheduleSlotBody | null;
  slot2: MusicScheduleSlotBody | null;
};

export function buildDeviceCommandRequest({
  deviceId,
  command,
  action,
  screensaverId,
  packageName,
  settings,
  timeZone,
  musicSchedule,
}: {
  deviceId: string;
  command: string;
  action?: "apply" | "enable" | "disable";
  screensaverId?: string;
  packageName?: string;
  settings?: DeviceCommandSettingsBody;
  timeZone?: string;
  musicSchedule?: MusicScheduleCommandBody;
}): {
  url: string;
  method: "POST";
  body: {
    command: string;
    action?: "apply" | "enable" | "disable";
    screensaverId?: string;
    packageName?: string;
    timeZone?: string;
    musicSchedule?: MusicScheduleCommandBody;
  } & DeviceCommandSettingsBody;
} {
  const body: {
    command: string;
    action?: "apply" | "enable" | "disable";
    screensaverId?: string;
    packageName?: string;
    timeZone?: string;
    musicSchedule?: MusicScheduleCommandBody;
  } & DeviceCommandSettingsBody = { command };
  if (action) body.action = action;
  if (screensaverId) body.screensaverId = screensaverId;
  if (packageName) body.packageName = packageName;
  if (timeZone) body.timeZone = timeZone;
  if (settings) {
    if (settings.volume !== undefined) body.volume = settings.volume;
    if (settings.master !== undefined) body.master = settings.master;
    if (settings.appLock !== undefined) body.appLock = settings.appLock;
    if (settings.appLockPin) body.appLockPin = settings.appLockPin;
    if (settings.wakeSleep !== undefined) body.wakeSleep = settings.wakeSleep;
    if (settings.startTime) body.startTime = settings.startTime;
    if (settings.endTime) body.endTime = settings.endTime;
    if (settings.sleepSaturday !== undefined) {
      body.sleepSaturday = settings.sleepSaturday;
    }
    if (settings.sleepSunday !== undefined) body.sleepSunday = settings.sleepSunday;
  }
  // Fleet parseUpdateMusicSchedule expects body.musicSchedule (nested).
  // Do not flatten onto the root — that breaks validation and device meta.
  if (musicSchedule) {
    (body as { musicSchedule?: MusicScheduleCommandBody }).musicSchedule =
      musicSchedule;
  }
  return {
    url: `v1/sa/device-fleet/${encodeURIComponent(deviceId)}/command`,
    method: "POST",
    body,
  };
}

/** GET /v1/sa/device-fleet/commands/{commandId} — operatorState poll. */
export function buildDeviceCommandStatusPath(commandId: string): string {
  return `v1/sa/device-fleet/commands/${encodeURIComponent(commandId)}`;
}

/** Pure path for POST /v1/sa/device-fleet/devices/register (physical serial inventory). */
export function buildDevicesRegisterPath(): string {
  return "v1/sa/device-fleet/devices/register";
}

/** Pure path for POST /v1/sa/device-fleet/devices/{serial}/first-trust/open */
export function buildDevicesFirstTrustOpenPath(serial: string): string {
  return `v1/sa/device-fleet/devices/${encodeURIComponent(serial)}/first-trust/open`;
}

/** Pure path for POST /v1/sa/device-fleet/devices/{serial}/first-trust/revoke */
export function buildDevicesFirstTrustRevokePath(serial: string): string {
  return `v1/sa/device-fleet/devices/${encodeURIComponent(serial)}/first-trust/revoke`;
}

export {
  buildZoneChannelPath,
  buildAssignZoneChannelRequest,
};
export type {
  ZoneChannelAssignmentSnapshot,
  ZoneAuthorizedChannel,
} from "./zoneChannelAssignment";

export function buildLocationZoneEditRequest(input: {
  locationId: string;
  zoneId: string;
  locationName: string;
  expectedLocationName: string;
  storeNumber: string;
  expectedStoreNumber: string;
  address: {
    street: string;
    city: string;
    state: string;
    zipCode: string;
    country: string;
  };
  expectedAddress: {
    street: string;
    city: string;
    state: string;
    zipCode: string;
    country: string;
  };
  zoneName: string;
  expectedZoneName: string;
}) {
  const { locationId, ...body } = input;
  return {
    url: `v1/sa/device-fleet/locations/${encodeURIComponent(locationId)}/details`,
    method: "PATCH" as const,
    body,
  };
}

/**
 * Pure path for GET /v1/sa/device-fleet/devices/{deviceId}
 * On-demand expanded Device Information (registry snapshot; no device command).
 */
export function buildDeviceDetailPath(deviceId: string): string {
  return `v1/sa/device-fleet/devices/${encodeURIComponent(deviceId)}`;
}

/** GET /v1/sa/device-fleet/devices/{deviceId}/screensaver-package */
export function buildScreensaverPackageStatusPath(deviceId: string): string {
  return `v1/sa/device-fleet/devices/${encodeURIComponent(deviceId)}/screensaver-package`;
}

/** GET /v1/sa/device-fleet/devices/{deviceId}/assistant-package */
export function buildAssistantPackageStatusPath(deviceId: string): string {
  return `v1/sa/device-fleet/devices/${encodeURIComponent(deviceId)}/assistant-package`;
}

/** GET /v1/sa/device-fleet/devices/{deviceId}/player-package */
export function buildPlayerPackageStatusPath(deviceId: string): string {
  return `v1/sa/device-fleet/devices/${encodeURIComponent(deviceId)}/player-package`;
}

/**
 * POST /v1/sa/device-fleet/devices/{deviceId}/software-inventory/refresh
 * Dedicated refresh endpoint (Fleet may also accept refresh via sendCommand).
 */
export function buildSoftwareInventoryRefreshPath(deviceId: string): string {
  return `v1/sa/device-fleet/devices/${encodeURIComponent(deviceId)}/software-inventory/refresh`;
}

/** Pure request builder for inventory refresh via sendCommand. */
export function buildRefreshSoftwareInventoryRequest(deviceId: string): {
  url: string;
  method: "POST";
  body: { command: "refresh_software_inventory" };
} {
  return buildDeviceCommandRequest({
    deviceId,
    command: "refresh_software_inventory",
  }) as {
    url: string;
    method: "POST";
    body: { command: "refresh_software_inventory" };
  };
}

/** Shared package-status response shape (Player / Assistant / Screensaver). */
export type DevicePackageStatusResponse = {
  success: boolean;
  deviceId: string;
  packageName: string;
  installed: {
    versionCode: number;
    versionName: string;
    displayVersion?: string | null;
  } | null;
  available: {
    versionCode: number;
    versionName: string;
    displayVersion?: string | null;
  } | null;
  updateAvailable: boolean;
  upToDate: boolean;
  observedAt?: string | null;
  lastReportedAt?: string | null;
  inventoryFresh?: boolean | null;
  inventoryStale?: boolean | null;
  inventory?: {
    observedAt?: string | null;
    fresh?: boolean | null;
    stale?: boolean | null;
    source?: string | null;
  } | null;
  updateCommand?: string;
  updateInstaller?: string;
  updateReceiverChannel?: string;
  screensaver_active_content_id?: string;
  screensaver_enabled?: boolean | null;
};

export const devicesApi = apiWithTags.injectEndpoints({
  endpoints: (builder) => ({
    /**
     * V2 SA human device registry list.
     * GET /v1/sa/device-fleet/devices — canonical kk-device joins.
     * Never call legacy GET /devices.
     */
    getDevices: builder.query<{ items: Device[] }, GetFiltersQueryArgs>({
      query: (args) => buildDevicesListPath(args),
      providesTags: ["Devices"],
    }),

    /**
     * On-demand expanded Device Information for one device.
     * Skipped until the row expands; no Android command; no polling.
     */
    getDeviceDetail: builder.query<DeviceDetailResponse, string>({
      query: (deviceId) => buildDeviceDetailPath(deviceId),
      keepUnusedDataFor: DEVICE_DETAIL_KEEP_UNUSED_DATA_SECONDS,
      // Payment entitlement lives on device detail — invalidate after music-service mutations.
      providesTags: (_result, _error, deviceId) => [
        { type: "DeviceDetail" as const, id: deviceId },
      ],
    }),

    /**
     * Register a physical Player by manufacturer serial (staging-only backend).
     * No deviceId / androidId / public key — inventory only.
     */
    registerPhysicalDevice: builder.mutation<
      {
        success: boolean;
        serialNumber: string;
        inventoryStatus: string;
        device: Device;
      },
      { serialNumber: string; model?: string }
    >({
      query: ({ serialNumber, model }) => ({
        url: buildDevicesRegisterPath(),
        method: "POST",
        body: model ? { serialNumber, model } : { serialNumber },
      }),
      invalidatesTags: ["Devices"],
    }),

    /**
     * Open a server-side First Trust window for a serial.
     * No secret is returned.
     */
    issueFirstTrust: builder.mutation<
      {
        success: boolean;
        serialNumber: string;
        firstTrustStatus: string;
        firstTrustWindowId: string;
        expiresAt: string;
        purpose?: string;
        assignmentIntent?: string | null;
        targetMasterAccountId?: string | null;
        targetLocationId?: string | null;
        targetZoneId?: string | null;
        stage: string;
        environment?: string;
        message?: string;
      },
      {
        serialNumber: string;
        purpose?: string;
        assignmentIntent?: "KEEP_EXISTING" | "CHANGE_ASSIGNMENT";
        masterAccountId?: string;
        locationId?: string;
        zoneId?: string;
      }
    >({
      query: ({
        serialNumber,
        purpose,
        assignmentIntent,
        masterAccountId,
        locationId,
        zoneId,
      }) => ({
        url: buildDevicesFirstTrustOpenPath(serialNumber),
        method: "POST",
        body: {
          ...(purpose ? { purpose } : {}),
          ...(assignmentIntent ? { assignmentIntent } : {}),
          ...(masterAccountId ? { masterAccountId } : {}),
          ...(locationId ? { locationId } : {}),
          ...(zoneId ? { zoneId } : {}),
        },
      }),
      invalidatesTags: ["Devices"],
    }),

    saveLocationZone: builder.mutation<
      { success: boolean; locationName: string; storeNumber: string; zoneName: string },
      Parameters<typeof buildLocationZoneEditRequest>[0]
    >({
      query: (input) => buildLocationZoneEditRequest(input),
      invalidatesTags: ["Devices"],
    }),

    getLocationZones: builder.query<
      {
        locationId: string;
        zones: Array<{
          zoneId: string;
          name?: string;
          status?: string;
          locationId?: string;
          channelId?: string | null;
          occupied?: boolean;
        }>;
      },
      string
    >({
      query: (locationId) =>
        `v1/sa/device-fleet/locations/${encodeURIComponent(locationId)}/zones`,
    }),

    revokeFirstTrust: builder.mutation<
      {
        success: boolean;
        serialNumber: string;
        firstTrustStatus: string;
        stage: string;
        message?: string;
      },
      { serialNumber: string; reason?: string }
    >({
      query: ({ serialNumber, reason }) => ({
        url: buildDevicesFirstTrustRevokePath(serialNumber),
        method: "POST",
        body: {
          ...(reason ? { reason } : {}),
        },
      }),
      invalidatesTags: ["Devices"],
    }),

    /**
     * V2 remote command to exactly ONE device.
     * POST /v1/sa/device-fleet/{deviceId}/command  body: { command }
     * UI must pass a string deviceId — never androidId, never APK URL.
     */
    sendDeviceCommand: builder.mutation<
      {
        deviceId: string;
        channel: string;
        command: string;
        apkVersionCode: number | null;
        commandId?: string;
        operatorState?: string;
      },
      {
        deviceId: string;
        command: string;
        action?: "apply" | "enable" | "disable";
        screensaverId?: string;
        packageName?: string;
        settings?: DeviceCommandSettingsBody;
        timeZone?: string;
        musicSchedule?: MusicScheduleCommandBody;
      }
    >({
      query: (args) => buildDeviceCommandRequest(args),
    }),

    getDeviceCommandStatus: builder.query<
      {
        command: {
          operatorState?: string;
          commandId?: string;
          activeContentId?: string | null;
          activeContentVersion?: string | null;
          terminalOutcome?: string | null;
          targetApp?: string;
        };
      },
      { commandId: string }
    >({
      query: ({ commandId }) => buildDeviceCommandStatusPath(commandId),
    }),

    getScreensaverPackageStatus: builder.query<
      DevicePackageStatusResponse,
      { deviceId: string }
    >({
      query: ({ deviceId }) => buildScreensaverPackageStatusPath(deviceId),
      providesTags: ["Devices"],
    }),

    getAssistantPackageStatus: builder.query<
      DevicePackageStatusResponse,
      { deviceId: string }
    >({
      query: ({ deviceId }) => buildAssistantPackageStatusPath(deviceId),
      providesTags: ["Devices"],
    }),

    getPlayerPackageStatus: builder.query<
      DevicePackageStatusResponse,
      { deviceId: string }
    >({
      query: ({ deviceId }) => buildPlayerPackageStatusPath(deviceId),
      providesTags: ["Devices"],
    }),

    /**
     * Request a fresh device software inventory observation.
     * Primary path: sendCommand refresh_software_inventory (device channel → Assistant).
     * Invalidates package GETs so Installed/Available refetch after the ask.
     */
    refreshSoftwareInventory: builder.mutation<
      {
        deviceId: string;
        channel?: string;
        command: string;
        commandId?: string;
        operatorState?: string;
      },
      { deviceId: string }
    >({
      query: ({ deviceId }) => buildRefreshSoftwareInventoryRequest(deviceId),
      invalidatesTags: ["Devices"],
    }),

    /**
     * Alternate POST refresh endpoint when Fleet exposes a dedicated route.
     * Prefer refreshSoftwareInventory (sendCommand) unless callers need this path.
     */
    postSoftwareInventoryRefresh: builder.mutation<
      { success?: boolean; deviceId?: string; message?: string },
      { deviceId: string }
    >({
      query: ({ deviceId }) => ({
        url: buildSoftwareInventoryRefreshPath(deviceId),
        method: "POST",
        body: {},
      }),
      invalidatesTags: ["Devices"],
    }),

    previewMusicService: builder.mutation<Record<string, unknown>, Record<string, unknown>>({
      query: (body) => ({
        url: "v1/sa/device-fleet/music-service/preview",
        method: "POST",
        body,
      }),
    }),
    suspendMusicService: builder.mutation<Record<string, unknown>, Record<string, unknown>>({
      query: (body) => ({
        url: "v1/sa/device-fleet/music-service/suspend",
        method: "POST",
        body,
      }),
      // Refetch Devices list + every cached DeviceDetail so Payment updates without a browser refresh.
      invalidatesTags: ["Devices", { type: "DeviceDetail" }],
    }),
    restoreMusicService: builder.mutation<Record<string, unknown>, Record<string, unknown>>({
      query: (body) => ({
        url: "v1/sa/device-fleet/music-service/restore",
        method: "POST",
        body,
      }),
      invalidatesTags: ["Devices", { type: "DeviceDetail" }],
    }),
    listMusicService: builder.query<Record<string, unknown>, string>({
      query: (masterAccountId) =>
        `v1/sa/device-fleet/music-service?masterAccountId=${encodeURIComponent(masterAccountId)}`,
    }),

    /**
     * Zone music channel assignment + authorized picker catalog.
     * GET /v1/sa/device-fleet/zones/{zoneId}/channel?includeAuthorized=true
     */
    getZoneChannelAssignment: builder.query<
      ZoneChannelAssignmentSnapshot,
      { zoneId: string; includeAuthorized?: boolean }
    >({
      query: ({ zoneId, includeAuthorized = true }) =>
        buildZoneChannelPath(zoneId, { includeAuthorized }),
    }),

    /**
     * Persist Zone channel + targeted notify_kk_channel_assignment.
     * UI must poll GET until device ACK confirms for the target device.
     */
    assignZoneChannel: builder.mutation<
      ZoneChannelAssignmentSnapshot,
      {
        zoneId: string;
        channelId: string;
        expectedRevision?: number | null;
        retryNotify?: boolean;
      }
    >({
      query: (input) => buildAssignZoneChannelRequest(input),
      invalidatesTags: ["Devices"],
    }),
  }),
});
export const {
  useGetDevicesQuery,
  useGetDeviceDetailQuery,
  useRegisterPhysicalDeviceMutation,
  useIssueFirstTrustMutation,
  useRevokeFirstTrustMutation,
  useGetLocationZonesQuery,
  useSaveLocationZoneMutation,
  useSendDeviceCommandMutation,
  useLazyGetDeviceCommandStatusQuery,
  useGetScreensaverPackageStatusQuery,
  useLazyGetScreensaverPackageStatusQuery,
  useGetAssistantPackageStatusQuery,
  useLazyGetAssistantPackageStatusQuery,
  useGetPlayerPackageStatusQuery,
  useLazyGetPlayerPackageStatusQuery,
  useRefreshSoftwareInventoryMutation,
  usePostSoftwareInventoryRefreshMutation,
  usePreviewMusicServiceMutation,
  useSuspendMusicServiceMutation,
  useRestoreMusicServiceMutation,
  useLazyListMusicServiceQuery,
  useGetZoneChannelAssignmentQuery,
  useLazyGetZoneChannelAssignmentQuery,
  useAssignZoneChannelMutation,
} = devicesApi;
